import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ArrowRight, Tag } from 'lucide-react';
import MarketingLayout from '@/components/MarketingLayout';

// Sample blog posts (these would come from backend/CMS in production)
const BLOG_POSTS = [
  {
    id: 'freelance-administratie-tips',
    slug: 'freelance-administratie-tips',
    titleNl: '10 Tips voor een Efficiënte Freelance Administratie',
    titleEn: '10 Tips for Efficient Freelance Administration',
    excerptNl: 'Als freelancer is een goede administratie essentieel. Ontdek onze beste tips om je urenregistratie en facturatie te stroomlijnen.',
    excerptEn: 'As a freelancer, good administration is essential. Discover our best tips to streamline your time tracking and invoicing.',
    category: 'tips',
    date: '2025-12-15',
    readTime: 5
  },
  {
    id: 'belastingaangifte-2025',
    slug: 'belastingaangifte-2025',
    titleNl: 'Belastingaangifte 2025: Wat Freelancers Moeten Weten',
    titleEn: 'Tax Return 2025: What Freelancers Need to Know',
    excerptNl: 'De belastingaangifte voor 2025 komt eraan. Leer welke aftrekposten je als freelancer kunt claimen en hoe je optimaal voordeel haalt.',
    excerptEn: 'The 2025 tax return is coming. Learn what deductions you can claim as a freelancer and how to maximize your benefits.',
    category: 'belasting',
    date: '2025-12-10',
    readTime: 8
  },
  {
    id: 'kilometerregistratie-belastingdienst',
    slug: 'kilometerregistratie-belastingdienst',
    titleNl: 'Kilometerregistratie: Wat Eist de Belastingdienst?',
    titleEn: 'Mileage Registration: What Does the Tax Authority Require?',
    excerptNl: 'Een correcte kilometerregistratie is verplicht voor de Belastingdienst. Ontdek welke gegevens je moet bijhouden en hoe je dit het beste aanpakt.',
    excerptEn: 'Proper mileage registration is mandatory for the tax authority. Discover what data you need to keep and how to best handle this.',
    category: 'belasting',
    date: '2025-12-05',
    readTime: 6
  },
  {
    id: 'uurtarief-berekenen',
    slug: 'uurtarief-berekenen',
    titleNl: 'Hoe Bereken Je Je Uurtarief als Freelancer?',
    titleEn: 'How to Calculate Your Hourly Rate as a Freelancer?',
    excerptNl: 'Het bepalen van je uurtarief is een van de belangrijkste beslissingen als freelancer. Wij leggen uit hoe je tot een eerlijk en winstgevend tarief komt.',
    excerptEn: 'Determining your hourly rate is one of the most important decisions as a freelancer. We explain how to arrive at a fair and profitable rate.',
    category: 'business',
    date: '2025-11-28',
    readTime: 7
  },
  {
    id: 'factuur-eisen',
    slug: 'factuur-eisen',
    titleNl: 'Welke Gegevens Moeten op een Factuur?',
    titleEn: 'What Information Must Be on an Invoice?',
    excerptNl: 'Een factuur moet aan bepaalde wettelijke eisen voldoen. Ontdek alle verplichte elementen voor een geldige factuur.',
    excerptEn: 'An invoice must meet certain legal requirements. Discover all mandatory elements for a valid invoice.',
    category: 'tips',
    date: '2025-11-20',
    readTime: 4
  },
  {
    id: 'werk-prive-balans',
    slug: 'werk-prive-balans',
    titleNl: 'Werk-Privé Balans als Freelancer: 5 Praktische Tips',
    titleEn: 'Work-Life Balance as a Freelancer: 5 Practical Tips',
    excerptNl: 'Als freelancer kan het lastig zijn om werk en privé gescheiden te houden. Deze tips helpen je een gezonde balans te vinden.',
    excerptEn: 'As a freelancer, it can be difficult to separate work and personal life. These tips will help you find a healthy balance.',
    category: 'lifestyle',
    date: '2025-11-15',
    readTime: 5
  }
];

const CATEGORIES = [
  { id: 'all', labelNl: 'Alles', labelEn: 'All' },
  { id: 'tips', labelNl: 'Tips', labelEn: 'Tips' },
  { id: 'belasting', labelNl: 'Belasting', labelEn: 'Tax' },
  { id: 'business', labelNl: 'Business', labelEn: 'Business' },
  { id: 'lifestyle', labelNl: 'Lifestyle', labelEn: 'Lifestyle' }
];

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const isNl = i18n.language === 'nl';

  const filteredPosts = selectedCategory === 'all'
    ? BLOG_POSTS
    : BLOG_POSTS.filter(post => post.category === selectedCategory);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(isNl ? 'nl-NL' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat ? (isNl ? cat.labelNl : cat.labelEn) : categoryId;
  };

  return (
    <MarketingLayout
      title={t('blog.meta.title')}
      description={t('blog.meta.description')}
      keywords="zzp blog, freelancer tips, urenregistratie nieuws, administratie artikelen"
    >
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-20 pb-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('blog.hero.title')}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('blog.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Blog List */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 justify-center mb-12">
            {CATEGORIES.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className={selectedCategory === cat.id 
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500' 
                  : 'border-slate-300'
                }
              >
                {isNl ? cat.labelNl : cat.labelEn}
              </Button>
            ))}
          </div>

          {/* Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {filteredPosts.map((post) => (
              <Card key={post.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden group">
                {/* Placeholder image */}
                <div className="aspect-video bg-gradient-to-br from-cyan-500/20 to-teal-500/20 relative">
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium text-slate-700">
                      <Tag className="w-3 h-3" />
                      {getCategoryLabel(post.category)}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(post.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {post.readTime} min
                    </span>
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-cyan-600 transition-colors">
                    <Link to={`/blog/${post.slug}`}>
                      {isNl ? post.titleNl : post.titleEn}
                    </Link>
                  </h2>
                  <p className="text-slate-600 mb-4 line-clamp-2">
                    {isNl ? post.excerptNl : post.excerptEn}
                  </p>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center text-cyan-600 font-medium hover:text-cyan-700"
                  >
                    {t('blog.readMore')}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500">{t('blog.noPosts')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-gradient-to-r from-cyan-500 to-teal-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            {t('blog.newsletter.title')}
          </h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            {t('blog.newsletter.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder={t('blog.newsletter.placeholder')}
              className="flex-1 px-4 py-3 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-white"
            />
            <Button className="bg-slate-900 hover:bg-slate-800 text-white px-6">
              {t('blog.newsletter.button')}
            </Button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
